import { z } from 'zod';

/* ===========================================================================
 * WHEN A SITE'S PROJECT MANAGERS HEAR ABOUT IT — `notifications.siteManagers`.
 *
 * Its own file because `settings.schema.ts` is past its line budget and may
 * only shrink (the same move `settings.appLock.schema.ts` made). Read by
 * `packages/api/src/jobs/siteManagerDigest.job.ts`; every time here is a
 * wall-clock time in the COMPANY timezone, never the server's or a browser's
 * (docs/time-and-dates.md).
 *
 * The two summaries and the alert share one window: the morning summary and
 * the alert are only sent before the evening summary's time, so a server that
 * was down all day does not post a "morning" summary at 22:00.
 * ======================================================================== */

/** "HH:MM", 24-hour. Local to this file so it does not import its own parent. */
const HHMM = /^([01]?\d|2[0-3]):[0-5]\d$/u;

export const SITE_MANAGER_ALERT_DEFAULTS = {
  morningSummaryAt: '09:00',
  eveningSummaryAt: '18:00',
  lowAttendanceAfter: '10:00',
  lowAttendanceBelowPercent: 80,
} as const;

export const SiteManagerAlertsSchema = z
  .object({
    /** The morning summary: in, late, on leave and absent against assigned. */
    morningSummaryAt: z.string().regex(HHMM, 'Expected HH:MM').default(SITE_MANAGER_ALERT_DEFAULTS.morningSummaryAt),
    /** The evening summary: checked out, still in, and who came. */
    eveningSummaryAt: z.string().regex(HHMM, 'Expected HH:MM').default(SITE_MANAGER_ALERT_DEFAULTS.eveningSummaryAt),
    /** No low-attendance alert before this time — the crew is still arriving. */
    lowAttendanceAfter: z.string().regex(HHMM, 'Expected HH:MM').default(SITE_MANAGER_ALERT_DEFAULTS.lowAttendanceAfter),
    /** Alert when present is under this share of the assigned crew. 0 turns the alert off. */
    lowAttendanceBelowPercent: z.number().int().min(0).max(100).default(SITE_MANAGER_ALERT_DEFAULTS.lowAttendanceBelowPercent),
  })
  .default(() => ({ ...SITE_MANAGER_ALERT_DEFAULTS }));
export type SiteManagerAlerts = z.infer<typeof SiteManagerAlertsSchema>;
